<div class="rx-trends-container" [class.rx-trends-container--modal]="isModal">
  <div class="rx-trends-header">
    <div class="rx-trends-title-group">
      <ion-icon name="bar-chart-outline" class="rx-trends-title-icon"></ion-icon>
      <ion-label class="rx-trends-title">{{ 'RX_TRENDS' | translate }}</ion-label>
    </div>

    <div class="rx-trends-toggles" *ngIf="!restricted">
      <ion-segment class="rx-trends-segment" [value]="periodMode" mode="ios"
        (ionChange)="onPeriodModeChange($event.detail.value)">
        <ion-segment-button value="weekly">
          <ion-label>{{ 'RX_TRENDS_WEEKLY' | translate }}</ion-label>
        </ion-segment-button>
        <ion-segment-button value="monthly">
          <ion-label>{{ 'RX_TRENDS_MONTHLY' | translate }}</ion-label>
        </ion-segment-button>
      </ion-segment>

      <ion-segment class="rx-trends-segment" [value]="view" mode="ios"
        (ionChange)="onViewChange($event.detail.value)">
        <ion-segment-button value="graph" layout="icon-start">
          <ion-icon name="bar-chart-outline" class="segment-icon"></ion-icon>
          <ion-label>{{ 'RX_TRENDS_GRAPH' | translate }}</ion-label>
        </ion-segment-button>
        <ion-segment-button value="table" layout="icon-start">
          <ion-icon name="reorder-three-outline" class="segment-icon"></ion-icon>
          <ion-label>{{ 'RX_TRENDS_TABLE' | translate }}</ion-label>
        </ion-segment-button>
      </ion-segment>

      <!-- Expand/close: the modal presents this same component with isModal=true, so this button only ever shows one of the two.
           Hidden while awaiting data: `componentProps` assignment fires no
           ngOnChanges, so a modal opened now would hold its inputs frozen and
           sit on the placeholder even after the card behind it has filled in. -->
      <button *ngIf="!isModal && !isAwaitingData" type="button" class="rx-trends-icon-btn" (click)="openFullscreen()"
        [attr.aria-label]="'RX_TRENDS_EXPAND' | translate">
        <ion-icon name="expand-outline"></ion-icon>
      </button>
      <button *ngIf="isModal" type="button" class="rx-trends-icon-btn" (click)="closeFullscreen()"
        [attr.aria-label]="'RX_TRENDS_CLOSE' | translate">
        <ion-icon name="close-outline"></ion-icon>
      </button>
    </div>
  </div>

  <!-- PDRP / restricted-access state -->
  <div *ngIf="restricted" class="rx-trends-message">
    {{ restrictionMessage }}
  </div>

  <!-- Territory ingest still paging while figures are already on screen: a note
       under them, since the card has content to keep rather than a state to
       swap. Nothing on screen yet is the skeleton's job instead — see
       `isAwaitingData`. -->
  <div *ngIf="isRefreshingInBackground && !isModal"
    class="rx-trends-message rx-trends-message-inline">
    {{ 'RX_TRENDS_SYNCING' | translate }}
  </div>

  <ng-container *ngIf="!restricted">
    <!-- Modal only: inline, the journey tab already renders this row above the card. -->
    <product-pill-row *ngIf="isModal" [pills]="productPills" [activePillId]="activePillId"
      (pillSelect)="onPillSelect($event)"></product-pill-row>

    <!-- The newest bucket can lag the source extract by weeks, so say when the
         figures are from. Rendered in whichever row leads the card — the metric
         row in the All view, the chip row otherwise — so it always sits outside
         the chip scroller and never scrolls away with the chips. -->
    <ng-template #dataAsOfNote>
      <div *ngIf="dataAsOfDate" class="rx-trends-legend-notes">
        <span class="legend-note legend-note-as-of">
          {{ 'RX_TRENDS_DATA_AS_OF' | translate: { date: dataAsOfDate } }}
        </span>
      </div>
    </ng-template>

    <ng-template #swatch let-color="color" let-lineStyleType="lineStyleType">
      <span class="legend-swatch" [class.legend-swatch-line]="lineStyleType !== 'solid'"
        [style.background]="lineStyleType === 'solid' ? color : null"
        [style.border-top-color]="lineStyleType !== 'solid' ? color : null"
        [style.border-top-style]="lineStyleType !== 'solid' ? lineStyleType : null"></span>
    </ng-template>

    <!-- All view only: the lines are products, so one metric is charted at a time. -->
    <div *ngIf="isAllProducts" class="rx-trends-metric-row">
      <span class="rx-trends-metric-label">{{ 'RX_TRENDS_METRIC' | translate }}</span>
      <ion-segment class="rx-trends-segment rx-trends-segment--compact" [value]="selectedMetric" mode="ios"
        (ionChange)="onMetricChange($event.detail.value)">
        <ion-segment-button *ngFor="let metric of metricOrder" [value]="metric">
          <ion-label>{{ metricLabel(metric) }}</ion-label>
        </ion-segment-button>
      </ion-segment>
      <ng-container [ngTemplateOutlet]="dataAsOfNote"></ng-container>
    </div>

    <div class="rx-trends-legend">
      <!-- Scrolls horizontally rather than wrapping: the All view puts one chip
           per product here, which on a wide territory would otherwise push the
           chart down by several rows. -->
      <div class="rx-trends-legend-chips">
        <button type="button" class="legend-chip" *ngFor="let entry of legendEntries"
          [class.legend-chip-off]="!entry.visible" (click)="onToggleLegendEntry(entry.key)">
          <ng-container [ngTemplateOutlet]="swatch"
            [ngTemplateOutletContext]="{ color: entry.color, lineStyleType: entry.lineStyleType }"></ng-container>
          <span class="legend-label">{{ entry.label }}</span>
        </button>

        <!-- Sits with the chips it explains, and only where those chips are the
             three metrics: in the All view the lines are products, so how one
             metric relates to another says nothing about what is plotted. -->
        <span *ngIf="!isAllProducts" class="legend-note legend-note-formula">
          {{ 'RX_TRENDS_TRX_FORMULA' | translate }}
        </span>
      </div>

      <ng-container *ngIf="!isAllProducts" [ngTemplateOutlet]="dataAsOfNote"></ng-container>
    </div>

    <!-- Only a terminal "nothing here" once both the read and the ingest have
         finished; while either is outstanding the placeholder below is what
         shows, so the card never says "no data" about data still arriving. -->
    <div *ngIf="!hasSeriesForProduct && !isLoading && !isAwaitingData" class="rx-trends-message">
      {{ 'RX_TRENDS_NO_DATA' | translate }}
    </div>

    <!-- Said in words in both views. The graph's placeholder bars read as an
         empty chart on their own, and Table has no placeholder at all. -->
    <div *ngIf="isAwaitingData" class="rx-trends-message"
      [class.rx-trends-message-inline]="view === 'graph'">
      {{ 'RX_TRENDS_SYNCING' | translate }}
    </div>

    <!-- Rendered while loading or waiting on the ingest too, so the chart area
         exists to host the placeholder and reserve its height. -->
    <ng-container *ngIf="hasSeriesForProduct || isLoading || (isAwaitingData && view === 'graph')">
      <!--
        Pan-smoothness tuning for the low end of the device fleet (tested on
        iPad 6 / A10, devicePixelRatio 2). Both are the levers to dial if pan
        feel needs adjusting; see the docs on those inputs in ind-trend-chart.ts.

        maxDevicePixelRatio 1.5 — a DPR-2 screen would otherwise repaint 4x the
          pixels of a 1x one on every pan tick. 1.5 cuts that ~44%; drop to 1
          for ~75% at a visible cost in line/label crispness.
        panThrottleMs 16 — caps redraws at ~60fps. Unthrottled is smoothest on
          fast hardware, but on an A10 each repaint can exceed a frame budget,
          so pointer events pile up and the chart trails the finger. A bounded,
          evenly-spaced rate reads better here. Set to null to compare.
      -->
      <!--
        The chart is never *removed* while the skeleton shows — it is faded out
        underneath it. Hiding it with ngIf or display:none would give ECharts a
        zero-width host to initialise against, which produces a zero-size canvas
        that never recovers, so the skeleton would wait for a paint that can't
        happen.
      -->
      <div *ngIf="view === 'graph'" class="rx-trends-chart-area"
        [style.min-height.px]="chartHeight">
        <ind-trend-chart class="rx-trends-chart" [class.rx-trends-chart--settling]="showSkeleton"
          [series]="chartSeries" [categories]="categories"
          [height]="chartHeight"
          [windowMode]="chartWindowMode" [initialVisibleCategories]="chartInitialVisibleCategories"
          [maxDevicePixelRatio]="1.5" [panThrottleMs]="16" [yAxisMinInterval]="1"
          [tooltipOverflowLabel]="tooltipOverflowLabel"
          [noDataText]="'RX_TRENDS_NO_DATA' | translate"
          [yAxisTitle]="'RX_TRENDS_AXIS_PRESCRIPTIONS' | translate"
          [xAxisTitle]="(periodMode === 'weekly' ? 'RX_TRENDS_AXIS_WEEK' : 'RX_TRENDS_AXIS_MONTH') | translate"
          (painted)="onChartPainted()">
        </ind-trend-chart>

        <div *ngIf="showSkeleton" class="rx-trends-skeleton" [attr.aria-busy]="true"
          [attr.aria-label]="'RX_TRENDS_LOADING' | translate">
          <!-- Bars of uneven height read as "a chart is coming", where a plain
               block reads as a broken image. -->
          <!-- `skeleton-text-animated` is set by hand: `animated` alone is a
               no-op under IonicModule.forRoot({ animated: false }). -->
          <div class="rx-trends-skeleton-plot">
            <ion-skeleton-text animated class="skeleton-text-animated" *ngFor="let bar of skeletonBarHeights"
              [style.height.%]="bar"></ion-skeleton-text>
          </div>
          <ion-skeleton-text animated class="rx-trends-skeleton-axis skeleton-text-animated"></ion-skeleton-text>
        </div>
      </div>

      <div *ngIf="view === 'table'" class="rx-trends-table-wrapper allow-scroll-bar">
        <table class="rx-trends-table">
          <thead>
            <tr>
              <th class="rx-trends-head-cell rx-trends-cell-period" scope="col">
                {{ 'RX_TRENDS_PERIOD' | translate }}
              </th>
              <th class="rx-trends-head-cell" scope="col" *ngFor="let column of tableColumns">
                <span class="metric-head">
                  <ng-container [ngTemplateOutlet]="swatch"
                    [ngTemplateOutletContext]="{ color: column.color, lineStyleType: column.lineStyleType }"></ng-container>
                  <span>{{ column.label }}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of tableRows" [class.is-latest]="row.isLatest">
              <td class="rx-trends-cell-period">
                <span class="period-cell">
                  <span>{{ row.label }}</span>
                  <span *ngIf="row.isLatest" class="latest-badge">{{ 'RX_TRENDS_LATEST' | translate }}</span>
                </span>
              </td>
              <td *ngFor="let value of row.values">
                {{ value === null ? '-' : value }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ng-container>
  </ng-container>
</div>
